import { Outlet, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/useAuth'
import { PublicHeader } from '@/components/organisms/public-header'
import { SiteFooter } from '@/components/organisms/SiteFooter'

/**
 * The help centre's frame: marketing chrome, reading surface.
 *
 * ## Why this is not `PublicLayout`
 *
 * `PublicLayout` wraps its whole tree in `chrome-on-dark dark` and paints
 * `--surface-public`. That is right for a landing page and a price table, which
 * are short, image-led and deliberately dramatic.
 *
 * A help article is eight hundred words of Norwegian that somebody reads while
 * something is going wrong. Long-form body copy on the marketing surface is the
 * one place that design language stops helping, and it is also the place a
 * reader is least willing to squint. Dark navigation over a light reading area
 * is what every documentation site worth copying does, for this reason.
 *
 * So the header and the footer are the SAME components the rest of the public
 * site uses — no third chrome, no second nav to keep in sync, and both bring
 * their own `chrome-on-dark` background with them — while the content between
 * them sits on `--surface-page` and follows the reader's own light/dark choice.
 *
 * ## The one thing to not do here
 *
 * Do not add a `dark` class to this wrapper. The theme store is the sole owner
 * of that class on the document element (`docs/THEMING.md`); a second writer is
 * exactly the defect the theme audit closed in august 2026, where `dark:` was
 * bound to the OS instead of to the user's choice and the app switched half way.
 */
export function HelpLayout() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { t } = useTranslation()
  /* Signed in, the reader came from a screen in the app and wants to get back to it, not to the
     marketing site's «Dashbord» (remaining-surfaces review 25.10.2026). */
  const back = () => (window.history.length > 1 ? navigate(-1) : navigate('/dashboard'))
  return (
    <div className="h-full overflow-y-auto flex flex-col bg-[var(--surface-page)]">
      <PublicHeader />
      {/* The gutters live HERE, not on the pages.
          `check-page-shell` requires a page's root to be `PageShell`, which
          owns width but deliberately not padding, so a page that adds its own
          wrapper to get breathing room is choosing a container the gate exists
          to take away from it. Measured at 375px before this line existed:
          «Hjelpesenter» started at x=0, flush against the bezel. */}
      <main id="main-content" className="flex-1 px-4 py-10 sm:px-6 sm:py-14">
        {user && (
          <div className="mx-auto mb-6 max-w-[var(--content-width)]">
            <Button variant="ghost" size="sm" onClick={back}>
              <ArrowLeft className="size-4" aria-hidden="true" />
              {t('help.backToApp')}
            </Button>
          </div>
        )}
        <Outlet />
      </main>
      <SiteFooter />
    </div>
  )
}
